import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";

interface AdminOrderActionsModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  currentStatus: string;
  notifyCustomer: boolean | null;
  onClose: () => void;
  onChooseNotifyCustomer: (checked: boolean) => void;
  onConfirm: () => void;
  onDecline: () => void;
  onModify: () => void;
}

export const AdminOrderActionsModal: React.FC<AdminOrderActionsModalProps> = ({
  isOpen,
  isSubmitting,
  currentStatus,
  notifyCustomer,
  onClose,
  onChooseNotifyCustomer,
  onConfirm,
  onDecline,
  onModify,
}) => {
  const hasChosenNotificationPreference = notifyCustomer !== null;
  const isDeclined = currentStatus === "declined";
  // Declined orders have already lost their original QB Time event, so the safe recovery
  // path is to recreate the delivery instead of offering the normal confirm/modify actions.
  const primaryActionLabel = isDeclined ? "Recreate & Confirm" : "Confirm Order";
  const modifyHelpText = isDeclined
    ? "Recreation keeps the original delivery time when it is still available. If it has passed or is occupied, you will be asked to select a new time."
    : "Modified orders follow the notification schedule. If a published delivery must return to pending, QuickBooks Time archives it and creates a replacement draft.";

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={isOpen}
      onClose={onClose}
      aria-labelledby="admin-order-actions-title"
    >
      <DialogTitle id="admin-order-actions-title">
        Manage Customer Response
      </DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          Use this when the customer missed the delivery confirmation email or
          SMS and you need to record the response from admin.
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Current status: <strong>{currentStatus.toUpperCase()}</strong>
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {modifyHelpText}
        </Typography>
        <FormControl component="fieldset" style={{ marginTop: "12px" }}>
          <FormLabel component="legend">
            Choose notification behavior before taking an action
          </FormLabel>
          {/* We ask for notify vs do-not-notify up front because the action buttons make
              a real order change, and it is too easy to miss a small checkbox afterward. */}
          <RadioGroup
            value={
              notifyCustomer === null
                ? ""
                : notifyCustomer
                ? "notify"
                : "silent"
            }
            onChange={(event) =>
              onChooseNotifyCustomer(event.target.value === "notify")
            }
          >
            <FormControlLabel
              value="notify"
              control={<Radio color="primary" />}
              label="Notify customer after this action"
            />
            <FormControlLabel
              value="silent"
              control={<Radio color="primary" />}
              label="Do not notify customer"
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions style={{ justifyContent: "space-between", padding: "16px 24px" }}>
        <Button onClick={onClose} color="secondary" disabled={isSubmitting}>
          Cancel
        </Button>
        <div style={{ display: "flex", gap: "8px" }}>
          {!isDeclined && (
            <Button
              onClick={onDecline}
              color="secondary"
              disabled={isSubmitting || !hasChosenNotificationPreference}
            >
              Decline Order
            </Button>
          )}
          {!isDeclined && (
            <Button
              onClick={onModify}
              color="default"
              disabled={isSubmitting || !hasChosenNotificationPreference}
            >
              Modify Order
            </Button>
          )}
          <Button
            onClick={onConfirm}
            color="primary"
            variant="contained"
            disabled={isSubmitting || !hasChosenNotificationPreference}
          >
            {primaryActionLabel}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};
